import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
	ExportCarouselDto,
	GenerateCarouselCaptionDto,
	GenerateCarouselDto,
	GenerateContentDto,
	GenerateContentV2Dto,
	GenerateImageFromPostDto,
	RegenerateCarouselSlideDto,
	UpdateFileDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useMediaActions = () => {
	const queryClient = useQueryClient();
	const { id: companyID } = useCompany();
	const emit = useAlertEmit();

	const invalidateMedia = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.List(companyID),
			}),
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.All(companyID),
			}),
		]);
	}, [queryClient, companyID]);

	const update = useMutation({
		mutationFn: async (data: { id: number; dto: UpdateFileDto }) => {
			const updatedDto = await Api.UpdateFile(data.id, data.dto);
			queryClient.refetchQueries({
				queryKey: QueryKeys.Files.Single(companyID, data.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: "File has been updated.",
			});

			return updatedDto.data;
		},
	});

	const remove = useMutation({
		mutationFn: async (id: number) => {
			await Api.DeleteFile(id);
			await invalidateMedia();
			emit({
				type: AlertType.SUCCESS,
				content: "File has been removed.",
			});
		},
		onError: (error) =>
			emit({
				type: AlertType.ERROR,
				content:
					parseError(error) ?? "Could not delete this file. Please try again.",
			}),
	});

	const generate = useMutation({
		mutationFn: async (dto: GenerateContentDto) => {
			const res = await Api.GenerateImages(dto);

			return res.data;
		},
	});

	const saveGenerated = useMutation({
		mutationFn: async (urls: string[]) => {
			const allSettled = await Promise.allSettled(
				urls.map((url) => Api.SaveImageFromUrl({ url })),
			);
			const allSaved = !allSettled.some((r) => r.status !== "fulfilled");

			if (allSaved) {
				emit({
					type: AlertType.SUCCESS,
					content: "Successfully saved all selected images",
				});
			} else {
				emit({
					type: AlertType.ERROR,
					content: "Failed to save all selected images",
				});
			}

			await invalidateMedia();
		},
	});

	const generateV2 = useMutation({
		mutationFn: async (dto: GenerateContentV2Dto) => {
			const res = await Api.GenerateImagesV2(dto);
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate image. Please try again.",
			});
		},
	});

	const saveGeneratedV2 = useMutation({
		mutationFn: async (imageUrl: string) => {
			const res = await Api.SaveGeneratedV2(imageUrl);
			emit({
				type: AlertType.SUCCESS,
				content: "Image saved to media library.",
			});
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to save image.",
			});
		},
	});

	const generateFromPost = useMutation({
		mutationFn: async (dto: GenerateImageFromPostDto) => {
			const res = await Api.GenerateImageFromPost(dto);
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate image from post. Please try again.",
			});
		},
	});

	const generateCarousel = useMutation({
		mutationFn: async (dto: GenerateCarouselDto) => {
			const res = await Api.GenerateCarousel(dto);
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to generate carousel. Please try again.",
			});
		},
	});

	const regenerateCarouselSlide = useMutation({
		mutationFn: async (dto: RegenerateCarouselSlideDto) => {
			const res = await Api.RegenerateCarouselSlide(dto);
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to regenerate slide. Please try again.",
			});
		},
	});

	const exportCarousel = useMutation({
		mutationFn: async (dto: ExportCarouselDto) => {
			const res = await Api.ExportCarousel(dto);
			return res.data;
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to export carousel. Please try again.",
			});
		},
	});

	const generateCarouselCaption = useMutation({
		mutationFn: async (dto: GenerateCarouselCaptionDto) => {
			const res = await Api.GenerateCarouselCaption(dto);
			return res.data;
		},
	});

	return {
		invalidateMedia,
		update,
		remove,
		generate,
		saveGenerated,
		generateV2,
		saveGeneratedV2,
		generateFromPost,
		generateCarousel,
		regenerateCarouselSlide,
		exportCarousel,
		generateCarouselCaption,
	};
};
